import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export const usePhotoUpload = () => {
  const [uploading, setUploading] = useState(false);
  const { toast } = useToast();

  const uploadPhoto = async (
    file: File,
    clientId: string,
    siteId?: string | null,
  ): Promise<string | null> => {
    try {
      setUploading(true);

      // Validate file type
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
      if (!validTypes.includes(file.type)) {
        toast({
          variant: 'destructive',
          title: 'Invalid file type',
          description: 'Please upload a JPEG, PNG, or WebP image'
        });
        return null;
      }

      // Validate file size (5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast({
          variant: 'destructive',
          title: 'File too large',
          description: 'Photo must be less than 5MB'
        });
        return null;
      }

      // Resolve site_id (required for site-scoped upload RLS)
      let effectiveSiteId = siteId ?? null;
      if (!effectiveSiteId) {
        const { data: clientRow } = await supabase
          .from('clients')
          .select('site_id')
          .eq('id', clientId)
          .maybeSingle();
        effectiveSiteId = clientRow?.site_id ?? null;
      }

      if (!effectiveSiteId) {
        toast({
          variant: 'destructive',
          title: 'Cannot upload photo',
          description: 'Client is not assigned to a facility',
        });
        return null;
      }

      // Create unique filename scoped under the site folder so storage RLS
      // can verify the uploader is assigned to that site.
      const fileExt = file.name.split('.').pop();
      const fileName = `${clientId}-${Date.now()}.${fileExt}`;
      const filePath = `${effectiveSiteId}/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('client-photos')
        .upload(filePath, file, {
          upsert: true,
          contentType: file.type
        });

      if (uploadError) throw uploadError;

      return filePath;
    } catch (error: any) {
      console.error('Error uploading photo:', error);
      toast({
        variant: 'destructive',
        title: 'Upload failed',
        description: error.message || 'Failed to upload photo'
      });
      return null;
    } finally {
      setUploading(false);
    }
  };

  const deletePhoto = async (photoUrl: string) => {
    try {
      // Photo paths now have the form `${site_id}/${file}` or legacy flat `${file}`.
      // Strip any URL prefix and keep the storage-relative path.
      const marker = '/object/';
      const idx = photoUrl.indexOf(marker);
      let filePath = photoUrl;
      if (idx !== -1) {
        // …/object/(public|sign|authenticated)/client-photos/<path>
        const after = photoUrl.slice(idx + marker.length);
        const bucketIdx = after.indexOf('client-photos/');
        if (bucketIdx !== -1) {
          filePath = after.slice(bucketIdx + 'client-photos/'.length).split('?')[0];
        }
      }

      const { error } = await supabase.storage
        .from('client-photos')
        .remove([filePath]);

      if (error) throw error;

      return true;
    } catch (error: any) {
      console.error('Error deleting photo:', error);
      toast({
        variant: 'destructive',
        title: 'Delete failed',
        description: error.message || 'Failed to delete photo'
      });
      return false;
    }
  };

  return {
    uploadPhoto,
    deletePhoto,
    uploading
  };
};
